import { useStorage } from '~Components/Storage';
import { useLicenses, useSelector } from '~Hooks';
import { CartItem } from '~Types';
import { gql, useMutation } from '@apollo/client';
import { SendSkvTransaction, SendSkvTransactionVariables } from './__queries__';
import { useDeviceHandler } from '~Components/DeviceProvider';

const SKV_URL = process.env.SKV_TRANSACTION_ENDPOINT!;

const SEND_TRANSCATION_MUTATION = gql`
  mutation SendSkvTransaction(
    $deviceId: String!
    $corporateId: String!
    $cashRegisterName: String!
    $brutto: Int!
    $vatRateToSum: Json!
    $printType: String!
    $receiptNumber: Int!
    $date: Float
  ) {
    sendSkvTransaction(
      deviceId: $deviceId
      corporateId: $corporateId
      cashRegisterName: $cashRegisterName
      brutto: $brutto
      vatRateToSum: $vatRateToSum
      printType: $printType
      receiptNumber: $receiptNumber
      date: $date
    ) {
      controlUnitSerial
      success
      response
      productionNumber
      message
      error
    }
  }
`;

interface SKVBody {
  deviceId: string;
  corporateId: string;
  cashRegisterName: string;
  brutto: number;
  vatRateToSum: Record<number, number>;
  printType: string;
  receiptNumber: number;
  date: number;
}

/**
 * Tries to sync order with Skatteverket
 */
export const useSKV = () => {
  const deviceHandler = useDeviceHandler();

  const [sendSkvTransaction] = useMutation<SendSkvTransaction, SendSkvTransactionVariables>(
    SEND_TRANSCATION_MUTATION,
  );

  const companyFlags = useSelector((state) => state.companyFlags);

  const [storage, updateStorage, loadingStorage] = useStorage();

  const syncOldWay = async (body: SKVBody) => {
    try {
      const result = await fetch(SKV_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const parsedResult = await result.json();

      if (!parsedResult.response) {
        throw new Error('Fetched faulty complianceId');
      }

      // JB 20230411: Update the new SKV storage fields if needed
      // When we feel confident that the SKV migration is stable, we can use the new fields directly
      if (
        (parsedResult.controlUnitSerial !== storage.taxComplianceControlUnitSerial ||
          parsedResult.productionNumber !== storage.taxComplianceProductionNumber) &&
        !loadingStorage
      ) {
        updateStorage({
          taxComplianceControlUnitSerial: parsedResult.controlUnitSerial,
          taxComplianceProductionNumber: parsedResult.productionNumber,
        });
      }

      return {
        success: parsedResult.success,
        errorMessage: parsedResult.message,
        controlUnitSerial: parsedResult.controlUnitSerial,
        productionNumber: parsedResult.productionNumber,
        complianceId: parsedResult.response,
      };
    } catch (error) {
      return {
        success: false,
        errorMessage: error,
      };
    }
  };

  /** Use our own mutation to sync orders to Skatteverket if we use new licenses flow since those companies have a different device setup */
  const syncNewWay = async (body: SKVBody) => {
    try {
      const { data } = await sendSkvTransaction({
        variables: body,
      });

      if (!data || !data.sendSkvTransaction.success) {
        throw new Error(data?.sendSkvTransaction.error || 'Failed to send SKV transaction');
      }

      const resData = data.sendSkvTransaction;

      if (
        (resData.controlUnitSerial !== storage.taxComplianceControlUnitSerial ||
          resData.productionNumber !== storage.taxComplianceProductionNumber) &&
        !loadingStorage
      ) {
        updateStorage({
          taxComplianceControlUnitSerial: resData.controlUnitSerial,
          taxComplianceProductionNumber: resData.productionNumber,
        });
      }

      return {
        success: resData.success,
        errorMessage: resData.message,
        controlUnitSerial: resData.controlUnitSerial,
        productionNumber: resData.productionNumber,
        complianceId: resData.response,
      };
    } catch (error) {
      return {
        success: false,
        errorMessage: error,
      };
    }
  };

  const syncSKV = async (
    date: number,
    items: Pick<CartItem, 'amount' | 'discount' | 'vatRate'>[],
    amount: number,
    receiptNumber: number,
  ) => {
    if (
      !storage.taxComplianceDeviceId ||
      !storage.taxComplianceCorporateId ||
      !storage.taxComplianceDeviceName
    ) {
      return {
        success: false,
        errorMessage: 'Device not set',
      };
    }

    const vatRateToSum = items.reduce((obj, item) => {
      if (!obj[item.vatRate]) {
        obj[item.vatRate] = 0;
      }

      obj[item.vatRate] += item.amount - item.discount;
      return obj;
    }, {} as Record<number, number>);

    /** Devices that has been migrated to the new licenses still needs to syncOldWay since their activation data lives in another service in backend */
    const shouldUseNewWay =
      companyFlags.useNewLicenses && !deviceHandler?.activeDevice?.isMigrated;

    const body = {
      deviceId: shouldUseNewWay
        ? deviceHandler?.activeDevice?.id || storage.taxComplianceDeviceId
        : storage.taxComplianceDeviceId,
      corporateId: storage.taxComplianceCorporateId,
      cashRegisterName: storage.taxComplianceDeviceName,
      brutto: amount,
      vatRateToSum,
      printType: 'Normal',
      receiptNumber,
      date,
    };

    const response = shouldUseNewWay ? await syncNewWay(body) : await syncOldWay(body);

    return response;
  };

  return syncSKV;
};
